
import formMixins from '@/mixins/formMixins'
import {
  isIncompleteOrder,
  orderDate,
  projectedCfsAtOrderTime,
  relationId as cfsRelationId
} from '@/api/OrderCfsProjection'

export default {
  name: 'OrderCfsCalculator',
  mixins: [formMixins],
  props: {
    formData: {
      type: Object,
      default: () => {}
    }
  },
  data () {
    return {
      entryMode: 'new',
      newCfsInput: '',
      editingNewCfs: false
    }
  },
  computed: {
    originalCfs () {
      return projectedCfsAtOrderTime({
        order: this.formData,
        orders: Object.values(this.$store.getters['orders/getAll'] || {}),
        currentCfs: this.runningCfsForDiversionPoint(this.formData.diversionPoint)
      })
    },
    availableTypes () {
      if (this.originalCfs > 0 || (!this.hasTargetDateTime && this.hasPendingScheduledWater)) {
        return ['Off', 'Increase', 'Decrease']
      }

      return ['On']
    },
    isOffOrder () {
      return this.formData.type === 'Off'
    },
    newCfs () {
      return Number(this.formData.new_cfs) || 0
    },
    netCfs () {
      return Number(this.formData.net_cfs) || 0
    },
    taskLabel () {
      if (this.formData.type === 'Off') {
        return 'Turn Off'
      }
      if (this.netCfs > 0 && this.originalCfs > 0) {
        return 'Increase'
      }
      if (this.netCfs < 0 && this.newCfs > 0) {
        return 'Decrease'
      }
      return 'Turn On'
    },
    taskColor () {
      return this.netCfs < 0 || this.formData.type === 'Off' ? 'deep-orange' : 'success'
    },
    currentCaption () {
      return this.originalCfs > 0 ? 'Running now' : 'Off - not running'
    },
    resultLabel () {
      if (this.formData.type === 'Off') {
        return 'Turning Off'
      }
      if (this.netCfs > 0 && this.originalCfs > 0) {
        return 'Increasing'
      }
      if (this.netCfs < 0 && this.newCfs > 0) {
        return 'Decreasing'
      }
      return 'Turning On'
    },
    resultIcon () {
      return this.netCfs < 0 || this.formData.type === 'Off' ? 'mdi-arrow-down' : 'mdi-arrow-up'
    },
    resultIconColor () {
      return this.netCfs < 0 || this.formData.type === 'Off' ? '#c2410c' : '#287a43'
    },
    hasTargetDateTime () {
      return !this.isEmpty(this.formData.run_date) && !this.isEmpty(this.formData.run_time)
    },
    hasPendingScheduledWater () {
      if (this.isEmpty(this.formData.diversionPoint)) {
        return false
      }

      const diversionPointId = String(this.relationId(this.formData.diversionPoint))

      return Object.values(this.$store.getters['orders/getAll'] || {}).some((order) => {
        const scheduledDate = orderDate(order)

        return isIncompleteOrder(order) &&
          String(this.relationId(order.diversionPoint)) === diversionPointId &&
          scheduledDate &&
          !Number.isNaN(scheduledDate.getTime()) &&
          ['On', 'Increase'].includes(order.type)
      })
    }
  },
  watch: {
    'formData.diversionPoint': {
      handler () {
        this.initializeType()
      },
      immediate: true
    },
    'formData.type': {
      handler () {
        this.editingNewCfs = false
        this.normalizeForType()
      },
      immediate: true
    },
    'formData.new_cfs': {
      handler (value) {
        if (!this.editingNewCfs) {
          this.newCfsInput = value || ''
        }
      },
      immediate: true
    },
    'formData.run_date' () {
      this.normalizeForType()
    },
    'formData.run_time' () {
      this.normalizeForType()
    },
    originalCfs () {
      this.normalizeForType()
    }
  },
  methods: {
    initializeType () {
      if (this.isEmpty(this.formData.diversionPoint)) {
        return
      }

      if (!this.availableTypes.includes(this.formData.type)) {
        this.$set(this.formData, 'type', this.availableTypes[0])
      }

      this.normalizeForType()
    },
    setType (type) {
      if (!type || !this.availableTypes.includes(type)) {
        return
      }

      this.$set(this.formData, 'type', type)
      this.$emit('emitUp', { event: 'setType', value: type })
      this.normalizeForType()
    },
    startNewCfsInput () {
      if (!this.isOffOrder) {
        this.editingNewCfs = true
      }
    },
    finishNewCfsInput () {
      this.editingNewCfs = false
      this.newCfsInput = this.formData.new_cfs || ''
    },
    setNewCfs (value) {
      if (this.isOffOrder) {
        this.editingNewCfs = false
        this.normalizeForType()
        return
      }

      this.entryMode = 'new'
      this.editingNewCfs = true
      this.newCfsInput = value
      const newCfs = this.numeric(value)
      const netCfs = newCfs - this.originalCfs

      this.applyCfs(newCfs, netCfs, 'new')
    },
    setNetCfs (value) {
      if (this.isOffOrder) {
        this.normalizeForType()
        return
      }

      this.entryMode = 'net'
      const netCfs = this.numeric(value)
      const newCfs = this.originalCfs + netCfs

      this.applyCfs(newCfs, netCfs, 'net')
    },
    normalizeForType () {
      if (this.isEmpty(this.formData.diversionPoint) || this.isEmpty(this.formData.type)) {
        return
      }

      if (!this.availableTypes.includes(this.formData.type)) {
        this.$set(this.formData, 'type', this.availableTypes[0])
        this.$emit('emitUp', { event: 'setType', value: this.availableTypes[0] })
      }

      if (this.formData.type === 'On') {
        const newCfs = this.numeric(this.formData.new_cfs || this.formData.flow)
        this.applyCfs(newCfs, newCfs, this.entryMode)
      } else if (this.formData.type === 'Off') {
        this.applyCfs(0, -this.originalCfs, 'system')
      } else {
        const currentNew = this.formData.new_cfs !== '' && this.formData.new_cfs !== null && this.formData.new_cfs !== undefined
          ? this.numeric(this.formData.new_cfs)
          : this.originalCfs
        this.applyCfs(currentNew, currentNew - this.originalCfs, this.entryMode)
      }
    },
    applyCfs (newCfs, netCfs, mode) {
      const originalCfs = this.roundCfs(this.originalCfs)
      const roundedNew = this.roundCfs(newCfs)
      const roundedNet = this.roundCfs(netCfs)

      this.setFormValue('original_cfs', this.formatCfs(originalCfs))
      this.setFormValue('new_cfs', this.formatCfs(roundedNew))
      this.setFormValue('net_cfs', this.formatCfs(roundedNet))
      this.setFormValue('cfs_entry_mode', mode)
      this.setFormValue('flow', this.legacyFlowForOrder(roundedNew, roundedNet))

      if (!this.editingNewCfs || this.isOffOrder) {
        this.newCfsInput = this.formatCfs(roundedNew)
      }
    },
    setFormValue (field, value) {
      if (this.formData[field] !== value) {
        this.$set(this.formData, field, value)
      }
    },
    legacyFlowForOrder (newCfs, netCfs) {
      if (this.formData.type === 'Off') {
        return this.formatSignedCfs(netCfs)
      }

      return this.formatCfs(newCfs)
    },
    runningCfsForDiversionPoint (diversionPoint) {
      if (this.isEmpty(diversionPoint)) {
        return 0
      }

      return this.$store.getters['usages/runningUsagesFor'](diversionPoint)
        .reduce((total, usage) => total + (Number(usage.flow) || 0), 0)
    },
    relationId (value) {
      return cfsRelationId(value)
    },
    numeric (value) {
      const number = Number(value)
      return Number.isFinite(number) ? number : 0
    },
    roundCfs (value) {
      return Math.round((Number(value) || 0) * 100) / 100
    },
    formatCfs (value) {
      return this.roundCfs(value).toFixed(2)
    },
    formatSignedCfs (value) {
      const rounded = this.roundCfs(value)
      return rounded > 0 ? `+${rounded.toFixed(2)}` : rounded.toFixed(2)
    }
  }
}
